import { useState } from "react";

import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { useConfirmationDialogContext } from "@shared";

import { useClientUploadLink } from "@/common/components/layout/components/dropzone/components/client-self-uploads/hooks/useClientUploadLink";
import { SmsSendDialog } from "@/pages/clients/client/components/tabs/notes-tab/components/sms-send/SmsSendDialog";
import { useTranslation } from "@/translations";

/**
 * Title of the SMS template auto-applied in the dispatch overlay after activation. Matches the
 * seeded "Upload-Link für Klient:in" SMS template; if no template with this title exists, the overlay
 * opens without a template applied.
 */
const KUNDENUPLOAD_AKTIVIEREN_TEMPLATE_TITLE = "Upload-Link für Klient:in";

interface Props {
  clientId: string;
}

export function UploadLinkControl({ clientId }: Props) {
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const { uploadLink, activate, deactivate, copyLink } = useClientUploadLink(clientId);
  const [isSmsDialogOpen, setIsSmsDialogOpen] = useState(false);

  const handleActivateClick = () => {
    showConfirmationDialog({
      dialogTitleText: t("dropzone.uploadLink.confirmActivateTitle"),
      dialogText: t("dropzone.uploadLink.confirmActivateText"),
      discardText: t("common.cancel"),
      agreeText: t("dropzone.uploadLink.confirmActivateConfirm"),
      onClose: (confirmed) => {
        if (confirmed) {
          activate.mutate(undefined, { onSuccess: () => setIsSmsDialogOpen(true) });
        }
      },
    });
  };

  const handleDeactivateClick = () => {
    showConfirmationDialog({
      dialogTitleText: t("dropzone.uploadLink.confirmDeactivateTitle"),
      dialogText: t("dropzone.uploadLink.confirmDeactivateText"),
      discardText: t("common.cancel"),
      agreeText: t("dropzone.uploadLink.confirmDeactivateConfirm"),
      agreeColor: "error",
      agreeIcon: <LockOutlinedIcon />,
      onClose: (confirmed) => {
        if (confirmed) {
          deactivate.mutate();
        }
      },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {uploadLink ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }} title={uploadLink}>
            {uploadLink}
          </Typography>
          <IconButton size="small" aria-label={t("dropzone.uploadLink.copyLink")} onClick={() => void copyLink()}>
            <ContentCopyRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<LockOutlinedIcon />}
          onClick={handleActivateClick}
          disabled={activate.isPending}
        >
          {t("dropzone.uploadLink.activate")}
        </Button>
      )}

      <Button
        variant="outlined"
        color="error"
        fullWidth
        startIcon={<LockOutlinedIcon />}
        onClick={handleDeactivateClick}
        disabled={!uploadLink || deactivate.isPending}
      >
        {t("dropzone.uploadLink.deactivate")}
      </Button>

      {isSmsDialogOpen && (
        <SmsSendDialog
          clientId={clientId}
          initialTemplateTitle={KUNDENUPLOAD_AKTIVIEREN_TEMPLATE_TITLE}
          onClose={() => setIsSmsDialogOpen(false)}
        />
      )}
    </Box>
  );
}
